import { FormEvent, useEffect, useState } from 'react';
import { api, MIN_PASSWORD, users } from '../api';
import { dayOnly, SUB_LABEL } from '../format';
import { useAuth } from '../auth';

// My account: change your own password. Also shown on its own, before anything else,
// when an admin has set the password and it must be changed.
export function Account({ forced = false }: { forced?: boolean }) {
  const { user, logout, passwordChanged } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (next !== again) return setError('The two new passwords are different.');
    setBusy(true);
    try {
      await users.changeOwnPassword(current, next);
      setDone(true);
      setCurrent(''); setNext(''); setAgain('');
      passwordChanged();
    } catch (err) {
      setError((err as Error).message);
    }
    setBusy(false);
  };

  const form = (
    <form className="card form narrow" onSubmit={submit}>
      <h2>{forced ? 'Choose your own password' : 'Change password'}</h2>
      {forced && <p className="muted small">Your password was set by an admin. Choose a new one that only you know before carrying on.</p>}
      <label>{forced ? 'Password you were given' : 'Current password'}
        <input id="pw-current" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
      </label>
      <label>New password <span className="muted small">(at least {MIN_PASSWORD} characters)</span>
        <input id="pw-new" type="password" autoComplete="new-password" required minLength={MIN_PASSWORD} value={next} onChange={(e) => setNext(e.target.value)} />
      </label>
      <label>New password again
        <input id="pw-again" type="password" autoComplete="new-password" required minLength={MIN_PASSWORD} value={again} onChange={(e) => setAgain(e.target.value)} />
      </label>
      {error && <p className="error" role="alert">{error}</p>}
      {done && !forced && <p className="notice" role="status">Password changed. You've been logged out on your other devices.</p>}
      <div className="actions">
        <button type="submit" className="primary" disabled={busy}>{busy ? 'Saving…' : 'Save new password'}</button>
        {forced && <button type="button" className="link" onClick={() => void logout()}>Log out</button>}
      </div>
    </form>
  );

  if (forced) return <div className="login"><main className="login-main">{form}</main></div>;
  return (
    <div className="page">
      <h1>My account</h1>
      <p className="muted">{user?.name} · {user?.email} · {user?.customerName}</p>
      {user?.impersonatedBy
        ? <p className="callout">You're viewing the app as this user, so their password can't be changed here.</p>
        : form}
      <MyTrackers />
    </div>
  );
}

// Self-install trackers on this account: paid until when, and which ones are no longer shown.
function MyTrackers() {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.subscriptions>> | null>(null);
  useEffect(() => { api.subscriptions().then(setData).catch(() => setData(null)); }, []);
  if (!data || data.vehicles.length === 0) return null;
  return (
    <section className="card my-trackers">
      <h2>Your trackers</h2>
      <ul>
        {data.vehicles.map((v) => {
          const s = v.subscription;
          return (
            <li key={v.id}>
              <span><strong>{v.name}</strong>{v.registration && <span className="muted small"> · {v.registration}</span>}</span>
              {s ? (
                <span className={`small sub-${s.status}`}>
                  {s.status === 'active' && `Tracking until ${dayOnly(s.paidUntil)}`}
                  {s.status === 'expiring' && `Ends ${dayOnly(s.paidUntil)} – renew to keep tracking`}
                  {s.status === 'grace' && `Ended ${dayOnly(s.paidUntil)}; hidden from the map on ${dayOnly(s.hiddenFrom)} unless renewed`}
                  {s.status === 'expired' && `${SUB_LABEL.expired}: not shown on the map since ${dayOnly(s.hiddenFrom)}`}
                </span>
              ) : <span className="small muted">–</span>}
            </li>
          );
        })}
      </ul>
      <p className="muted small">To renew, contact Fleet Functions. Your vehicle's history is kept and comes back when you renew.</p>
    </section>
  );
}
